/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, CalendarPlus } from 'lucide-react';
import { openBookingOrScroll } from './heroUtils';

/** Stato vuoto: nessuna visita in programma, CTA animata alla prenotazione. */
export const EmptyVisitHero = () => (
  <motion.div
    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
    data-testid="patient-hero-empty"
  >
    <Card className="relative overflow-hidden p-5 rounded-3xl border-primary/25 bg-gradient-to-br from-primary/12 via-primary/5 to-card">
      <motion.div aria-hidden
        className="absolute -top-14 -right-10 h-40 w-40 rounded-full bg-primary/25 blur-3xl"
        animate={{ scale: [1, 1.12, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="relative flex items-start gap-4">
        <motion.div
          initial={{ scale: 0.7, rotate: -10, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 15, delay: 0.1 }}
          className="h-12 w-12 rounded-2xl bg-primary/25 text-primary flex items-center justify-center flex-shrink-0"
        >
          <CalendarPlus className="h-5 w-5" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-primary/70 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Il tuo prossimo passo
          </div>
          <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight mt-1">
            Nessuna visita in programma
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Prenota un consulto in <b>3 click</b> — puoi anche usare i tuoi <b>2 orientamenti gratuiti</b> del mese.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-3 flex flex-wrap gap-2"
          >
            <Button
              size="sm"
              onClick={openBookingOrScroll}
              data-testid="patient-hero-book-cta"
            >
              <Sparkles className="h-4 w-4" /> Trova un medico
            </Button>
          </motion.div>
        </div>
      </div>
    </Card>
  </motion.div>
);
